import { useState, useEffect, useMemo, useRef } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import { FaPlay, FaPause, FaRedo, FaDice, FaArrowLeft, FaArrowRight } from "react-icons/fa";
import RecursionTree3D from "./RecursionTree3D";
import { generateMergeSortSteps, MERGE_SORT_CODE } from "./mergeSortSteps";
import "./MergeSort3D.css";

const DEFAULT_INPUT = "38, 27, 43, 3, 9, 82";
const MAX_LEN = 10; // keep the recursion tree legible
const FEEDBACK_URL = "https://forms.gle/Gcn9mcKUvH4fANAB9"; // existing feedback form

const parseInput = (text) =>
  text
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !Number.isNaN(n))
    .slice(0, MAX_LEN);

function MergeSort3D() {
  const [inputText, setInputText] = useState(DEFAULT_INPUT);
  const [array, setArray] = useState(() => parseInput(DEFAULT_INPUT));
  const [current, setCurrent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(800); // ms per step
  const timerRef = useRef(null);

  // Beta: first-visit interaction banner + a lightweight helpfulness rating.
  const [showIntro, setShowIntro] = useState(() => !localStorage.getItem("ms3d_seen_intro"));
  const [rating, setRating] = useState(() => localStorage.getItem("ms3d_rating") || null);

  const dismissIntro = () => {
    localStorage.setItem("ms3d_seen_intro", "1");
    setShowIntro(false);
  };
  const rate = (dir) => {
    localStorage.setItem("ms3d_rating", dir);
    setRating(dir);
  };

  const { steps, nodes } = useMemo(() => generateMergeSortSteps(array), [array]);
  const nodeById = useMemo(() => {
    const m = {};
    nodes.forEach((n) => (m[n.id] = n));
    return m;
  }, [nodes]);

  const step = steps[current] || steps[0];
  const atEnd = current >= steps.length - 1;
  const activeNode = step ? nodeById[step.activeId] : null;

  // Auto-advance while playing.
  useEffect(() => {
    if (!isPlaying) return undefined;
    if (atEnd) {
      setIsPlaying(false);
      return undefined;
    }
    timerRef.current = setTimeout(() => setCurrent((c) => Math.min(c + 1, steps.length - 1)), speed);
    return () => clearTimeout(timerRef.current);
  }, [isPlaying, current, atEnd, speed, steps.length]);

  // Keyboard shortcuts: ← / → step, space play-pause. Ignored while typing.
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        setIsPlaying(false);
        setCurrent((c) => Math.min(steps.length - 1, c + 1));
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        setIsPlaying(false);
        setCurrent((c) => Math.max(0, c - 1));
      } else if (e.code === "Space" || e.key === " ") {
        e.preventDefault();
        if (atEnd) {
          setCurrent(0);
          setIsPlaying(true);
        } else {
          setIsPlaying((p) => !p);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps.length, atEnd]);

  const build = () => {
    const parsed = parseInput(inputText);
    if (parsed.length === 0) return;
    setIsPlaying(false);
    setArray(parsed);
    setCurrent(0);
  };

  const randomize = () => {
    const len = 5 + Math.floor(Math.random() * 3); // 5–7 elements
    const rand = Array.from({ length: len }, () => Math.floor(Math.random() * 90) + 1);
    setInputText(rand.join(", "));
    setIsPlaying(false);
    setArray(rand);
    setCurrent(0);
  };

  const reset = () => {
    setIsPlaying(false);
    setCurrent(0);
  };
  const stepBack = () => {
    setIsPlaying(false);
    setCurrent((c) => Math.max(0, c - 1));
  };
  const stepForward = () => {
    setIsPlaying(false);
    setCurrent((c) => Math.min(steps.length - 1, c + 1));
  };

  return (
    <div className="ms3d-page">
      <div className="ms3d-header">
        <div className="ms3d-title">
          <span className="ms3d-badge">3D LAB</span>
          <h1>Merge Sort — Recursion in 3D</h1>
          <p>Watch the recursion tree grow and collapse in space, with the call stack and code in sync.</p>
        </div>
      </div>

      <div className="ms3d-toolbar">
        <div className="ms3d-input-group">
          <label>Array:</label>
          <input
            className="ms3d-input"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="comma-separated numbers"
          />
          <button className="ms3d-btn" onClick={build}>Build</button>
          <button className="ms3d-btn ghost" onClick={randomize} title="Random array">
            <FaDice /> Random
          </button>
        </div>

        <div className="ms3d-controls">
          <button className="ms3d-navbtn" onClick={stepBack} disabled={current === 0} title="Previous step (←)">
            <FaArrowLeft /> Prev
          </button>
          <button
            className="ms3d-ctrl play"
            onClick={() => (atEnd ? (reset(), setIsPlaying(true)) : setIsPlaying((p) => !p))}
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          <button className="ms3d-navbtn" onClick={stepForward} disabled={atEnd} title="Next step (→)">
            Next <FaArrowRight />
          </button>
          <button className="ms3d-ctrl" onClick={reset} title="Reset">
            <FaRedo />
          </button>
          <div className="ms3d-speed">
            <label>Speed</label>
            <input
              type="range"
              min="200"
              max="1500"
              step="100"
              value={1700 - speed}
              onChange={(e) => setSpeed(1700 - Number(e.target.value))}
            />
          </div>
        </div>
      </div>

      <p className="ms3d-kbd-hint">
        <kbd>←</kbd> <kbd>→</kbd> step · <kbd>space</kbd> play/pause · drag to orbit · grab the top node to move the whole tree
      </p>

      <div className="ms3d-body">
        {/* Left column: 3D recursion tree + 2D array strip */}
        <div className="ms3d-left">
          <div className="ms3d-scene">
            {steps.length > 0 && <RecursionTree3D nodes={nodes} step={step} n={array.length} />}
            {showIntro && (
              <div className="ms3d-intro">
                <span>
                  👋 This is a <b>live 3D scene</b> — drag to orbit, scroll to zoom, and grab the top node to move the tree.
                </span>
                <button onClick={dismissIntro} aria-label="Dismiss">✕</button>
              </div>
            )}
            <div className="ms3d-legend">
              <span><i style={{ background: "#fbbf24" }} /> executing</span>
              <span><i style={{ background: "#60a5fa" }} /> on call stack</span>
              <span><i style={{ background: "#22c55e" }} /> returned / sorted</span>
            </div>
            <div className="ms3d-progress">
              Step {current + 1} / {steps.length}
            </div>
          </div>

          <div className="ms3d-strip">
            <div className="ms3d-panel-title">
              Array{activeNode ? ` · working on subarray [${activeNode.lo}…${activeNode.hi}]` : ""}
            </div>
            <div className="ms3d-cells">
              {step?.array.map((v, idx) => {
                const inWindow = activeNode && idx >= activeNode.lo && idx <= activeNode.hi;
                const wrote = step.write === idx;
                return (
                  <div key={idx} className={`ms3d-cell ${inWindow ? "window" : ""} ${wrote ? "wrote" : ""}`}>
                    <span className="ms3d-cell-val">{v}</span>
                    <span className="ms3d-cell-idx">{idx}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right column: code, call stack, description */}
        <div className="ms3d-side">
          <div className="ms3d-code">
            <div className="ms3d-panel-title">Code</div>
            <SyntaxHighlighter
              language="javascript"
              style={vscDarkPlus}
              showLineNumbers
              wrapLines
              lineProps={(lineNumber) => ({
                style: {
                  display: "block",
                  background: lineNumber === step?.line ? "rgba(96, 165, 250, 0.22)" : "transparent",
                  borderLeft:
                    lineNumber === step?.line ? "3px solid #60a5fa" : "3px solid transparent",
                },
              })}
              customStyle={{ margin: 0, background: "transparent", fontSize: "0.82rem" }}
            >
              {MERGE_SORT_CODE}
            </SyntaxHighlighter>
          </div>

          <div className="ms3d-stack">
            <div className="ms3d-panel-title">Call Stack</div>
            <div className="ms3d-stack-list">
              {step?.stackIds.length ? (
                [...step.stackIds].reverse().map((id, idx) => {
                  const nd = nodeById[id];
                  return (
                    <div key={id} className={`ms3d-frame ${idx === 0 ? "top" : ""}`}>
                      <span className="ms3d-frame-name">mergeSort(lo={nd.lo}, hi={nd.hi})</span>
                      <span className="ms3d-frame-vals">[{step.array.slice(nd.lo, nd.hi + 1).join(", ")}]</span>
                    </div>
                  );
                })
              ) : (
                <div className="ms3d-frame empty">stack empty — sorting complete</div>
              )}
            </div>
          </div>

          <div className="ms3d-desc">
            <div className="ms3d-panel-title">What's happening</div>
            <p>{step?.description}</p>
          </div>
        </div>
      </div>

      {/* Beta feedback */}
      <div className="ms3d-beta">
        <div className="ms3d-beta-msg">
          <span className="ms3d-beta-tag">🧪 BETA</span>
          <span>
            <b>The 3D Lab is brand new.</b> Was this helpful? Your feedback decides what we build next.
          </span>
        </div>
        <div className="ms3d-beta-actions">
          {rating ? (
            <span className="ms3d-beta-thanks">
              {rating === "up" ? "Glad it helped! 🙌 " : "Thanks — sorry it fell short. "}
              <a href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer">Tell us more →</a>
            </span>
          ) : (
            <>
              <button className="ms3d-thumb" onClick={() => rate("up")}>👍 Helpful</button>
              <button className="ms3d-thumb" onClick={() => rate("down")}>👎 Not really</button>
            </>
          )}
        </div>
      </div>

      {/* Coming soon / vote next */}
      <div className="ms3d-soon">
        <span className="ms3d-soon-label">Coming soon to the 3D Lab:</span>
        <span className="ms3d-chip">Quick Sort</span>
        <span className="ms3d-chip">N-Queens · backtracking</span>
        <span className="ms3d-chip">Binary Search</span>
        <a className="ms3d-soon-vote" href={FEEDBACK_URL} target="_blank" rel="noopener noreferrer">
          Vote for what's next →
        </a>
      </div>
    </div>
  );
}

export default MergeSort3D;
